import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { X, Upload, Globe, Lock, Image as ImageIcon, Video } from 'lucide-react';
import { useHighlightsStore } from '../stores/StoreProvider';
import toast from 'react-hot-toast';

const HighlightUpload = observer(({ onUploadComplete, onCancel }) => {
  const highlightsStore = useHighlightsStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadHighlight = async () => {
    if (!selectedFile) {
      toast.error('Please select a photo or video first');
      return;
    }

    setUploading(true);
    try {
      await highlightsStore.createHighlight({
        file: selectedFile,
        title,
        description,
        is_public: isPublic
      });

      toast.success('Highlight uploaded successfully!');
      onUploadComplete?.();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload highlight');
    } finally {
      setUploading(false);
    }
  };

  const isVideo = selectedFile?.type?.startsWith('video/');
  const isImage = selectedFile?.type?.startsWith('image/');

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Add Highlight</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Media Selection */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Select Media</h4>
          {selectedFile ? (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {isVideo ? (
                    <Video className="text-blue-600" size={20} />
                  ) : (
                    <ImageIcon className="text-green-600" size={20} />
                  )}
                  <span className="text-sm text-gray-700">{selectedFile.name}</span>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              {previewUrl && (
                <div className="mt-3">
                  {isVideo ? (
                    <video
                      src={previewUrl}
                      className="w-full h-32 object-cover rounded"
                      controls
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded"
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-sm text-gray-600 mb-2">Choose a photo or video</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Choose from Library
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <input
              type="text"
              placeholder="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          <div>
            <button
              onClick={() => setIsPublic(!isPublic)}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {isPublic ? (
                <Globe className="text-green-600" size={16} />
              ) : (
                <Lock className="text-gray-600" size={16} />
              )}
              <span className="text-sm text-gray-700">
                {isPublic ? 'Public' : 'Private'}
              </span>
            </button>
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={uploadHighlight}
          disabled={!selectedFile || uploading}
          className={`w-full mt-6 py-3 px-4 rounded-lg font-semibold transition-colors ${
            !selectedFile || uploading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {uploading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Uploading...
            </div>
          ) : (
            'Upload Highlight'
          )}
        </button>
      </div>
    </div>
  );
});

export default HighlightUpload; 